import { useEffect, useState } from 'react';
import { useStoreOrders } from '../hooks/useStoreApi';
import { useOrderAnalytics } from '../hooks/useOrderAnalytics';
import { AdminTabEmpty } from './store/admin/AdminTabEmpty';
import {
  AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import {
  TrendingUp, DollarSign, Clock, ShoppingCart,
  Calendar, Activity, Award, Package, Users, X, Filter
} from 'lucide-react';

export const AnalyticsTab = ({ storeId, onRegisterRefresh, chatId }) => {
  const [selectedMetric, setSelectedMetric] = useState('orders'); // 'orders' or 'revenue'
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [showDateFilter, setShowDateFilter] = useState(false);

  // Fetch ALL orders including archived for complete analytics
  const { data: ordersData, loading, error, refetch } = useStoreOrders(storeId, null, chatId);
  const allOrders = ordersData?.orders || [];

  // Convert date strings to Date objects for the hook
  const startDateObj = startDate ? new Date(startDate) : null;
  const endDateObj = endDate ? new Date(endDate) : null;

  // Process analytics data with date filtering
  const analytics = useOrderAnalytics(allOrders, startDateObj, endDateObj);

  // Register refresh handler
  useEffect(() => {
    if (onRegisterRefresh) {
      onRegisterRefresh('analytics', refetch);
    }
  }, [onRegisterRefresh, refetch]);

  const clearDateFilter = () => {
    setStartDate('');
    setEndDate('');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-mekomit-500 mx-auto mb-4"></div>
          <p className="text-ink-muted">טוען נתונים...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <p className="text-danger-700 mb-4">שגיאה בטעינת הנתונים</p>
          <button
            onClick={refetch}
            className="px-4 py-2 bg-mekomit-500 text-white rounded-lg hover:bg-mekomit-600"
          >
            נסה שוב
          </button>
        </div>
      </div>
    );
  }

  if (!allOrders.length) {
    return (
      <AdminTabEmpty
        icon={TrendingUp}
        tone="info"
        title="ניתוח הנתונים יחכה כאן — ברגע שיתחילו להגיע הזמנות"
        body="כשתתחיל לקבל הזמנות, האזור הזה יתעורר לחיים: גרפים, מגמות וזיהוי המוצרים המובילים. זה המסך שיעזור לך לקבל החלטות חכמות על המלאי, המחירים והשעות."
        examples={[
          'גרף מגמת הזמנות לאורך הזמן — אילו ימים החזקים שלך',
          'אילו שעות הכי עמוסות — שווה לפתוח משבצות זמן מתאימות',
          'דירוג המוצרים המובילים והמוצרים שלא ממריאים',
        ]}
      />
    );
  }

  const CustomTooltip = ({ active, payload, label, isRevenue = false }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-mekomit-200 rounded-lg shadow-lg">
          <p className="font-semibold text-ink-strong mb-1">{payload[0]?.payload?.fullDate || label}</p>
          <p className="text-sm" style={{ color: payload[0].color }}>
            {isRevenue ? `הכנסות: ₪${payload[0].value.toLocaleString('he-IL')}` : `הזמנות: ${payload[0].value}`}
          </p>
        </div>
      );
    }
    return null;
  };

  const HourTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-mekomit-200 rounded-lg shadow-lg">
          <p className="font-semibold text-ink-strong mb-1">שעה: {payload[0]?.payload?.displayHour}</p>
          <p className="text-sm text-mekomit-700">
            הזמנות: {payload[0].value}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-4 md:space-y-6" dir="rtl">
      {/* Header with Date Filter */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-mekomit-900">ניתוח נתונים</h2>
          <p className="text-xs md:text-sm text-ink-muted mt-0.5 md:mt-1">
            {analytics.totalOrders} הזמנות {analytics.dateRange && (
              <span className="hidden sm:inline">({analytics.dateRange.start} - {analytics.dateRange.end})</span>
            )}
          </p>
        </div>

        {/* Date Filter Button */}
        <button
          onClick={() => setShowDateFilter(!showDateFilter)}
          className="flex items-center gap-2 px-3 md:px-4 py-2 bg-white border border-mekomit-200 rounded-lg hover:bg-mekomit-50 transition-colors w-fit"
        >
          <Filter className="w-4 h-4 text-mekomit-600" />
          <span className="text-sm font-semibold text-ink-strong">סינון תאריכים</span>
          {(startDate || endDate) && (
            <span className="w-2 h-2 bg-mekomit-500 rounded-full"></span>
          )}
        </button>
      </div>

      {/* Date Range Filter Panel */}
      {showDateFilter && (
        <div className="bg-white rounded-lg md:rounded-xl p-4 md:p-5 border border-mekomit-200 shadow-sm animate-slide-down">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm md:text-base font-bold text-ink-strong">בחר טווח תאריכים</h3>
            <button
              onClick={() => setShowDateFilter(false)}
              className="p-1 hover:bg-surface-muted rounded"
            >
              <X className="w-4 h-4 text-ink-muted" />
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs md:text-sm font-medium text-ink mb-1">מתאריך</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-3 py-2 border border-line-strong rounded-lg focus:ring-2 focus:ring-mekomit-500 focus:border-transparent text-sm"
              />
            </div>
            <div>
              <label className="block text-xs md:text-sm font-medium text-ink mb-1">עד תאריך</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full px-3 py-2 border border-line-strong rounded-lg focus:ring-2 focus:ring-mekomit-500 focus:border-transparent text-sm"
              />
            </div>
          </div>
          {(startDate || endDate) && (
            <button
              onClick={clearDateFilter}
              className="mt-3 text-xs md:text-sm text-mekomit-700 hover:text-mekomit-900 font-semibold"
            >
              נקה סינון
            </button>
          )}
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 md:gap-4">
        {/* Total Orders */}
        <div className="bg-gradient-to-br from-mekomit-50 to-mekomit-100 rounded-lg md:rounded-xl p-3 md:p-5 border border-mekomit-200 shadow-sm">
          <div className="flex items-center gap-1.5 md:gap-3 mb-1 md:mb-2">
            <div className="p-1 md:p-2 bg-mekomit-500 rounded-md md:rounded-lg">
              <ShoppingCart className="w-3 h-3 md:w-5 md:h-5 text-white" />
            </div>
            <div className="text-[10px] md:text-xs font-medium text-mekomit-700 tracking-wide">סה"כ הזמנות</div>
          </div>
          <div className="text-xl md:text-3xl font-bold text-mekomit-900">{analytics.totalOrders}</div>
          <div className="text-[9px] md:text-xs text-mekomit-600 mt-0.5 md:mt-1">
            {startDate || endDate ? 'בטווח הנבחר' : 'כולל ארכיון'}
          </div>
        </div>

        {/* Total Revenue */}
        <div className="bg-gradient-to-br from-success-50 to-success-100 rounded-lg md:rounded-xl p-3 md:p-5 border border-success-200 shadow-sm">
          <div className="flex items-center gap-1.5 md:gap-3 mb-1 md:mb-2">
            <div className="p-1 md:p-2 bg-success-500 rounded-md md:rounded-lg">
              <DollarSign className="w-3 h-3 md:w-5 md:h-5 text-white" />
            </div>
            <div className="text-[10px] md:text-xs font-medium text-success-700 tracking-wide">סה"כ הכנסות</div>
          </div>
          <div className="text-lg md:text-3xl font-bold text-success-900">
            ₪{analytics.totalRevenue.toLocaleString('he-IL', { maximumFractionDigits: 0 })}
          </div>
          <div className="text-[9px] md:text-xs text-success-600 mt-0.5 md:mt-1">כל ההזמנות</div>
        </div>

        {/* Average Order Value */}
        <div className="bg-gradient-to-br from-info-50 to-info-100 rounded-lg md:rounded-xl p-3 md:p-5 border border-info-200 shadow-sm">
          <div className="flex items-center gap-1.5 md:gap-3 mb-1 md:mb-2">
            <div className="p-1 md:p-2 bg-info-500 rounded-md md:rounded-lg">
              <TrendingUp className="w-3 h-3 md:w-5 md:h-5 text-white" />
            </div>
            <div className="text-[10px] md:text-xs font-medium text-info-700 tracking-wide">ממוצע</div>
          </div>
          <div className="text-lg md:text-3xl font-bold text-info-900">
            ₪{analytics.avgOrderValue.toLocaleString('he-IL', { maximumFractionDigits: 0 })}
          </div>
          <div className="text-[9px] md:text-xs text-info-600 mt-0.5 md:mt-1">ערך ממוצע</div>
        </div>

        {/* Peak Hour */}
        <div className="bg-gradient-to-br from-warning-50 to-warning-100 rounded-lg md:rounded-xl p-3 md:p-5 border border-warning-200 shadow-sm">
          <div className="flex items-center gap-1.5 md:gap-3 mb-1 md:mb-2">
            <div className="p-1 md:p-2 bg-warning-500 rounded-md md:rounded-lg">
              <Clock className="w-3 h-3 md:w-5 md:h-5 text-white" />
            </div>
            <div className="text-[10px] md:text-xs font-medium text-warning-700 tracking-wide">שעת שיא</div>
          </div>
          <div className="text-xl md:text-3xl font-bold text-warning-900">
            {analytics.peakOrderingHour?.displayHour || 'N/A'}
          </div>
          <div className="text-[9px] md:text-xs text-warning-600 mt-0.5 md:mt-1">
            {analytics.peakOrderingHour ? `${analytics.peakOrderingHour.count} הזמנות` : 'אין נתונים'}
          </div>
        </div>
      </div>

      {/* Metric Selector */}
      <div className="flex gap-1.5 md:gap-2 bg-white rounded-lg md:rounded-xl p-1.5 md:p-2 border border-mekomit-100 shadow-sm w-full md:w-fit">
        <button
          onClick={() => setSelectedMetric('orders')}
          className={`flex-1 md:flex-none px-3 md:px-4 py-2 rounded-md md:rounded-lg font-semibold text-xs md:text-sm transition-all ${selectedMetric === 'orders'
            ? 'bg-gradient-to-r from-mekomit-500 to-mekomit-400 text-white shadow-md'
            : 'text-ink hover:bg-mekomit-50'
            }`}
        >
          <div className="flex items-center justify-center gap-1.5 md:gap-2">
            <ShoppingCart className="w-3 h-3 md:w-4 md:h-4" />
            <span className="hidden sm:inline">הזמנות לאורך זמן</span>
            <span className="sm:hidden">הזמנות</span>
          </div>
        </button>
        <button
          onClick={() => setSelectedMetric('revenue')}
          className={`flex-1 md:flex-none px-3 md:px-4 py-2 rounded-md md:rounded-lg font-semibold text-xs md:text-sm transition-all ${selectedMetric === 'revenue'
            ? 'bg-gradient-to-r from-success-500 to-success-400 text-white shadow-md'
            : 'text-ink hover:bg-success-50'
            }`}
        >
          <div className="flex items-center justify-center gap-1.5 md:gap-2">
            <DollarSign className="w-3 h-3 md:w-4 md:h-4" />
            <span className="hidden sm:inline">הכנסות לאורך זמן</span>
            <span className="sm:hidden">הכנסות</span>
          </div>
        </button>
      </div>

      {/* Orders/Revenue Over Time Chart */}
      <div className="bg-white rounded-lg md:rounded-xl p-4 md:p-6 border border-mekomit-100 shadow-sm">
        <div className="flex items-center gap-2 mb-3 md:mb-4">
          <Activity className="w-4 h-4 md:w-5 md:h-5 text-mekomit-600" />
          <h3 className="text-base md:text-lg font-bold text-ink-strong">
            {selectedMetric === 'orders' ? 'מספר הזמנות לאורך זמן' : 'הכנסות לאורך זמן'}
          </h3>
        </div>
        <ResponsiveContainer width="100%" height={250}>
          {selectedMetric === 'orders' ? (
            <AreaChart data={analytics.ordersByDay}>
              <defs>
                <linearGradient id="colorOrders" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#fb923c" stopOpacity={0.8} />
                  <stop offset="95%" stopColor="#fb923c" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                dataKey="displayDate"
                stroke="#6b7280"
                style={{ fontSize: '12px' }}
              />
              <YAxis
                stroke="#6b7280"
                style={{ fontSize: '12px' }}
                allowDecimals={false}
              />
              <Tooltip content={<CustomTooltip isRevenue={false} />} />
              <Area
                type="monotone"
                dataKey="count"
                stroke="#fb923c"
                strokeWidth={2}
                fillOpacity={1}
                fill="url(#colorOrders)"
              />
            </AreaChart>
          ) : (
            <AreaChart data={analytics.revenueByDay}>
              <defs>
                <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#22c55e" stopOpacity={0.8} />
                  <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                dataKey="displayDate"
                stroke="#6b7280"
                style={{ fontSize: '12px' }}
              />
              <YAxis
                stroke="#6b7280"
                style={{ fontSize: '12px' }}
              />
              <Tooltip content={<CustomTooltip isRevenue={true} />} />
              <Area
                type="monotone"
                dataKey="revenue"
                stroke="#22c55e"
                strokeWidth={2}
                fillOpacity={1}
                fill="url(#colorRevenue)"
              />
            </AreaChart>
          )}
        </ResponsiveContainer>
      </div>

      {/* Orders by Hour Chart */}
      <div className="bg-white rounded-lg md:rounded-xl p-4 md:p-6 border border-mekomit-100 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3 md:mb-4">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 md:w-5 md:h-5 text-mekomit-600" />
            <h3 className="text-base md:text-lg font-bold text-ink-strong">דפוס הזמנות לפי שעה</h3>
          </div>
          {analytics.peakOrderingHour && (
            <div className="flex items-center gap-1.5 md:gap-2 px-2 md:px-3 py-1.5 md:py-2 bg-warning-50 border border-warning-200 rounded-md md:rounded-lg w-fit">
              <Award className="w-3 h-3 md:w-4 md:h-4 text-warning-600" />
              <span className="text-xs md:text-sm font-semibold text-warning-700">
                שעת שיא: {analytics.peakOrderingHour.displayHour}
              </span>
            </div>
          )}
        </div>
        <p className="text-xs md:text-sm text-ink-muted mb-3 md:mb-4">
          גלה מתי הלקוחות מזמינים הכי הרבה
        </p>
        <ResponsiveContainer width="100%" height={250}>
          <BarChart data={analytics.ordersByHour}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis
              dataKey="displayHour"
              stroke="#6b7280"
              style={{ fontSize: '11px' }}
              interval={1}
              angle={-45}
              textAnchor="end"
              height={80}
            />
            <YAxis
              stroke="#6b7280"
              style={{ fontSize: '12px' }}
              allowDecimals={false}
            />
            <Tooltip content={<HourTooltip />} />
            <Bar
              dataKey="count"
              fill="#fb923c"
              radius={[8, 8, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Product Analytics */}
      {analytics.productAnalytics.length > 0 && (
        <div className="bg-white rounded-lg md:rounded-xl p-4 md:p-6 border border-mekomit-100 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <Package className="w-4 h-4 md:w-5 md:h-5 text-mekomit-600" />
            <h3 className="text-base md:text-lg font-bold text-ink-strong">ניתוח מוצרים</h3>
          </div>
          <p className="text-xs md:text-sm text-ink-muted mb-4">המוצרים המובילים שלך</p>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-surface-muted border-b border-line">
                <tr>
                  <th className="text-right px-3 py-2 text-xs font-semibold text-ink">#</th>
                  <th className="text-right px-3 py-2 text-xs font-semibold text-ink">שם המוצר</th>
                  <th className="text-center px-3 py-2 text-xs font-semibold text-ink">כמות נמכרה</th>
                  <th className="text-center px-3 py-2 text-xs font-semibold text-ink">הזמנות</th>
                  <th className="text-center px-3 py-2 text-xs font-semibold text-ink">הכנסות</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line-subtle">
                {analytics.productAnalytics.slice(0, 10).map((product, idx) => (
                  <tr key={product.productId} className="hover:bg-surface-muted">
                    <td className="px-3 py-2 text-xs text-ink-muted">{idx + 1}</td>
                    <td className="px-3 py-2 text-sm font-medium text-ink-strong">{product.productName}</td>
                    <td className="px-3 py-2 text-center text-sm text-ink">{product.totalQuantity}</td>
                    <td className="px-3 py-2 text-center text-sm text-ink">{product.orderCount}</td>
                    <td className="px-3 py-2 text-center text-sm font-semibold text-success-700">
                      ₪{product.totalRevenue.toLocaleString('he-IL')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Customer Analytics */}
      {analytics.customerAnalytics.length > 0 && (
        <div className="bg-white rounded-lg md:rounded-xl p-4 md:p-6 border border-mekomit-100 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <Users className="w-4 h-4 md:w-5 md:h-5 text-mekomit-600" />
            <h3 className="text-base md:text-lg font-bold text-ink-strong">ניתוח לקוחות</h3>
          </div>
          <p className="text-xs md:text-sm text-ink-muted mb-4">הלקוחות המובילים שלך</p>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-surface-muted border-b border-line">
                <tr>
                  <th className="text-right px-3 py-2 text-xs font-semibold text-ink">#</th>
                  <th className="text-right px-3 py-2 text-xs font-semibold text-ink">שם לקוח</th>
                  <th className="text-right px-3 py-2 text-xs font-semibold text-ink hidden sm:table-cell">טלפון</th>
                  <th className="text-center px-3 py-2 text-xs font-semibold text-ink">הזמנות</th>
                  <th className="text-center px-3 py-2 text-xs font-semibold text-ink">סה"כ הוצאות</th>
                  <th className="text-center px-3 py-2 text-xs font-semibold text-ink hidden md:table-cell">ממוצע</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line-subtle">
                {analytics.customerAnalytics.slice(0, 15).map((customer, idx) => (
                  <tr key={customer.customerId} className="hover:bg-surface-muted">
                    <td className="px-3 py-2 text-xs text-ink-muted">{idx + 1}</td>
                    <td className="px-3 py-2 text-sm font-medium text-ink-strong">{customer.customerName}</td>
                    <td className="px-3 py-2 text-sm text-ink hidden sm:table-cell">{customer.customerPhone || '-'}</td>
                    <td className="px-3 py-2 text-center text-sm text-ink">{customer.orderCount}</td>
                    <td className="px-3 py-2 text-center text-sm font-semibold text-success-700">
                      ₪{customer.totalSpent.toLocaleString('he-IL')}
                    </td>
                    <td className="px-3 py-2 text-center text-sm text-ink-muted hidden md:table-cell">
                      ₪{customer.avgOrderValue.toLocaleString('he-IL')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Date Range Info */}
      {analytics.dateRange && (
        <div className="bg-gradient-to-r from-mekomit-50 to-mekomit-100 rounded-lg md:rounded-xl p-3 md:p-4 border border-mekomit-200">
          <div className="flex items-center gap-2 md:gap-3">
            <Calendar className="w-4 h-4 md:w-5 md:h-5 text-mekomit-600" />
            <div>
              <p className="text-sm md:text-base font-semibold text-ink-strong">טווח נתונים</p>
              <p className="text-xs md:text-sm text-ink-muted">
                {analytics.dateRange.start} עד {analytics.dateRange.end} ({analytics.dateRange.days} ימים)
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
